import * as React from "react"
import Modal from "react-modal"
import {cloneDeep, set} from "lodash"
import {withTranslation} from "react-i18next"
import {COUNTRIES} from "../../constants"
import {difference} from "../../util"
// import {updateBabyListInvoiceMeta} from '../../actions/baby-list-invoice'
import type {Dispatch, InvoicePerson} from "../../types"

type Props = {
  +t: any,
  +title: string,
  +person: InvoicePerson,
  +dispatch: Dispatch,
  +onCloseModal: (SyntheticEvent<*>) => void,
  +onAfterOpen: () => void,
  +onUpdate: (InvoicePerson) => void,
}

type State = {
  edits: InvoicePerson,
  errors: any,
}

class InvoicePersonEditModal extends React.Component<Props, State> {
  handleSave: (SyntheticEvent<HTMLButtonElement>) => void
  handleInputChange: (SyntheticInputEvent<HTMLInputElement>) => void

  static defaultProps = {
    title: "Edit",
  }

  constructor(props: Props) {
    super(props)
    this.state = {
      edits: {...props.person},
      errors: {},
    }
    Modal.setAppElement("#app")
    this.handleSave = this.handleSave.bind(this)
    this.handleInputChange = this.handleInputChange.bind(this)
  }

  handleInputChange(e: SyntheticInputEvent<HTMLInputElement>) {
    const target = e.target
    const value = target.type === "checkbox" ? target.checked : target.value
    const name = target.name
    this.edit(name, value)
  }

  handleSave(e: SyntheticInputEvent<HTMLButtonElement>) {
    const {edits} = this.state
    const {onSave, onCloseModal, person} = this.props
    const diff = difference(edits, person)
    if (Object.keys(diff).length > 0) {
      onSave(edits)
    }
    onCloseModal(e)
  }

  edit(property: string, value: any) {
    const edits = cloneDeep(this.state.edits)
    set(edits, property, value)
    this.setState({edits})
  }

  hasError(fieldName: string): boolean {
    return Object.keys(this.state.errors).includes(fieldName)
  }

  showError(fieldName: string): ?React.Node {
    const error = this.state.errors[fieldName]
    if (error) {
      return <div className="invalid-feedback">{error.type}</div>
    }
    return null
  }

  renderField(property, hasLabel = true, isRequired = false) {
    const {edits} = this.state
    const {t} = this.props
    const inputType = property.includes("email") ? "email" : "text"
    let invalidClass = this.hasError(property) ? "is-invalid" : ""
    let label = (
      <label key={`${property}-lbl`} htmlFor={property}>
        {t(`invoice.${property}`)}
        {isRequired && "*"}
      </label>
    )
    let field = null
    switch (property) {
      case "country":
        field = (
          <select
            className={"form-control " + invalidClass}
            value={edits[property] || "BE"}
            name={property}
            id={property}
            key={property}
            onChange={this.handleInputChange}
          >
            {COUNTRIES.map((c) => {
              return (
                <option key={`${property}-opt-${c}`} value={c}>
                  {t(`countries.${c}`)}
                </option>
              )
            })}
          </select>
        )
        break
      default:
        field = (
          <input
            className={"form-control " + invalidClass}
            name={property}
            id={property}
            key={property}
            type={inputType}
            value={edits[property] || ""}
            onChange={this.handleInputChange}
          />
        )
    }
    return (
      <>
        {hasLabel && label}
        {field}
        {this.hasError(property) && this.showError(property)}
      </>
    )
  }

  render() {
    const {t, title, person} = this.props
    return (
      <Modal
        className="modal-dialog modal-lg"
        isOpen={true}
        shouldCloseOnEsc={true}
        shouldCloseOnOverlayClick={true}
        onRequestClose={this.props.onCloseModal}
        onAfterOpen={this.props.onAfterOpen}
        contentLabel={title}
      >
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{title}</h5>
          </div>

          <div className="modal-body">
            {person.partner1Firstname ? (
              <>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      {this.renderField("partner1Firstname")}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      {this.renderField("partner1Lastname")}
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      {this.renderField("partner2Firstname")}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      {this.renderField("partner2Lastname")}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    {this.renderField("firstname")}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    {this.renderField("lastname")}
                  </div>
                </div>
              </div>
            )}

            <div className="row">
              <div className="col-md-8">
                <div className="form-group">{this.renderField("street")}</div>
              </div>
              <div className="col-md-2">
                <div className="form-group">{this.renderField("nbr")}</div>
              </div>
              <div className="col-md-2">
                <div className="form-group">{this.renderField("box")}</div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-3">
                <div className="form-group">
                  {this.renderField("postalCode")}
                </div>
              </div>
              <div className="col-md-5">
                <div className="form-group">{this.renderField("city")}</div>
              </div>
              <div className="col-md-4">
                <div className="form-group">{this.renderField("country")}</div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-4">
                <div className="form-group">{this.renderField("email")}</div>
              </div>
              <div className="col-md-4">
                <div className="form-group">{this.renderField("emailPartner")}</div>
              </div>
              <div className="col-md-4">
                <div className="form-group">{this.renderField("phone")}</div>
              </div>
            </div>

            {person.company && (
              <React.Fragment>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      {this.renderField("company")}
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-4">
                    <div className="form-group">{this.renderField("vat")}</div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      {this.renderField("accountNbr")}
                    </div>
                  </div>
                </div>
              </React.Fragment>
            )}

            <div className="d-flex align-items-center justify-content-between">
              <button className="btn btn-primary" onClick={this.handleSave}>
                {t("invoice.save")}
              </button>
              <a className="cancel" href="#" onClick={this.props.onCloseModal}>
                {t("invoice.cancel")}
              </a>
            </div>
          </div>
        </div>
      </Modal>
    )
  }
}

export default withTranslation()(InvoicePersonEditModal)
